import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, getToken, isMaster } from "../lib/api";
import TopNav from "../components/TopNav.jsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * OvenPage v0 — the app-builder-agent. Master-only "ask for a spec" form
 * on top; public build log below. Baker writes SPECS, not code. Every
 * spec lands here with cost, Warden verdict, and a plain-english letter.
 */
export default function OvenPage() {
  const [feature, setFeature] = useState("");
  const [notes, setNotes] = useState("");
  const [priority, setPriority] = useState("normal");
  const [kind, setKind] = useState("code"); // "code" (v1 · unified diff) | "spec" (v0 · markdown plan)
  const [contextFiles, setContextFiles] = useState("");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState(null);
  const [openSpec, setOpenSpec] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/sage/oven/build-log?limit=25`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setLog(await r.json());
    } catch (e) {
      setErr(e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const submitSpec = async (e) => {
    e?.preventDefault?.();
    if (!feature.trim()) { toast.error("Describe the feature first."); return; }
    setBusy(true);
    try {
      const url = kind === "code"
        ? `${BACKEND_URL}/api/sage/admin/oven/code`
        : `${BACKEND_URL}/api/sage/admin/oven/spec`;
      const body = kind === "code"
        ? {
            feature: feature.trim(),
            priority,
            context_files: contextFiles.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean),
          }
        : { feature: feature.trim(), notes: notes.trim() || null, priority };
      const r = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t.slice(0, 300));
      }
      const j = await r.json();
      toast.success(`${kind === "code" ? "Diff" : "Spec"} ready · $${(j.cost_usd ?? 0).toFixed(6)} · ${(j.duration_ms/1000).toFixed(2)}s`);
      setFeature(""); setNotes("");
      await load();
    } catch (e2) {
      toast.error(e2.message);
    } finally {
      setBusy(false);
    }
  };

  const markApplied = async (spec_id) => {
    if (!confirm("Mark this diff as applied? This is a public audit record — do it after you've pasted the diff into the codebase.")) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/sage/admin/oven/mark-applied/${spec_id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success("Marked applied on the Build Log.");
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative">
      <TopNav />
      <div className="mx-auto max-w-5xl px-4 md:px-8 py-16">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Baker · app-builder v3 · test bench</div>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter uppercase text-sage-text mb-6">
          Sage plans Sage.
        </h1>
        <p className="text-base text-sage-muted leading-relaxed max-w-3xl mb-8 sage-prose">
          v0 writes a plain-english plan. v1 writes the actual code. v2 checks the shape fits. v3 turns the engine on — boots a private copy of Sage with the new code inside, knocks on the door, records whether the new thing answered. Every result on the public Build Log.
        </p>
        <p className="text-sm text-sage-muted leading-relaxed max-w-3xl mb-12 sage-prose">
          Every AI product hides its build process. Ours writes it in the open.
        </p>

        {isMaster() && (
          <div className="border border-sage-border p-6 mb-12" data-testid="oven-spec-form">
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-faint mb-3">Master · ask Baker</div>

            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setKind("code")}
                data-testid="oven-kind-code"
                className={`text-[10px] uppercase tracking-[0.15em] px-3 py-2 border ${kind === "code" ? "border-sage-accent text-sage-accent" : "border-sage-border text-sage-muted hover:border-sage-text"}`}
              >
                v1 · code diff
              </button>
              <button
                type="button"
                onClick={() => setKind("spec")}
                data-testid="oven-kind-spec"
                className={`text-[10px] uppercase tracking-[0.15em] px-3 py-2 border ${kind === "spec" ? "border-sage-accent text-sage-accent" : "border-sage-border text-sage-muted hover:border-sage-text"}`}
              >
                v0 · markdown spec
              </button>
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint self-center ml-2">
                {kind === "code" ? "Sage writes actual code · Warden grades · you paste it in" : "Sage writes the plan · not the code"}
              </div>
            </div>

            <form onSubmit={submitSpec} className="flex flex-col gap-3">
              <textarea
                data-testid="oven-feature-input"
                value={feature}
                onChange={(e) => setFeature(e.target.value)}
                rows={3}
                placeholder={kind === "code"
                  ? "e.g. Add a GET /api/sage/ping endpoint in /app/backend/server.py that returns {ok:true, at:iso}"
                  : "e.g. Add a Refund History page that shows every refund in the last 90 days publicly"}
                className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text placeholder-sage-faint focus:outline-none focus:border-sage-accent resize-y"
              />

              {kind === "code" ? (
                <input
                  data-testid="oven-context-files"
                  type="text"
                  value={contextFiles}
                  onChange={(e) => setContextFiles(e.target.value)}
                  placeholder="Context files (optional, comma-separated paths) — e.g. /app/backend/server.py, /app/frontend/src/App.js"
                  className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-xs text-sage-muted placeholder-sage-faint focus:outline-none focus:border-sage-accent"
                />
              ) : (
                <textarea
                  data-testid="oven-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Notes (optional) — constraints, references, links"
                  className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-xs text-sage-muted placeholder-sage-faint focus:outline-none focus:border-sage-accent resize-y"
                />
              )}

              <div className="flex flex-wrap gap-4 items-center">
                <select
                  data-testid="oven-priority-select"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="bg-sage-bg border border-sage-border px-3 py-2 text-xs text-sage-muted focus:outline-none focus:border-sage-accent"
                >
                  <option value="small">small</option>
                  <option value="normal">normal</option>
                  <option value="large">large</option>
                </select>
                <button
                  type="submit"
                  data-testid="oven-submit"
                  disabled={busy || !feature.trim()}
                  className="text-xs uppercase tracking-[0.1em] border border-sage-accent text-sage-accent px-4 py-2 hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {busy ? (kind === "code" ? "Writing the diff…" : "Writing the spec…") : (kind === "code" ? "Write the code →" : "Write the spec →")}
                </button>
              </div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint">
                {kind === "code"
                  ? "Baker v1 refuses to touch the sealed core (founder_identity, ledger, Warden, refunds, silicon-stakes)."
                  : "v0 files a plan. v1 files an actual diff you can apply."}
              </div>
            </form>
          </div>
        )}

        {err && (
          <div className="mb-6 border border-sage-warn p-3 text-xs text-sage-warn" data-testid="oven-build-log-error">
            {err}
          </div>
        )}

        <div className="border border-sage-border p-6" data-testid="oven-build-log">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-6">
            <div className="text-xs uppercase tracking-[0.2em] text-sage-muted">Build Log · every spec on the record</div>
            {log?.totals && (
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint tabular-nums">
                {log.totals.specs} specs · {log.totals.clean_rate_pct}% clean · ${log.totals.total_cost_usd.toFixed(6)} total
              </div>
            )}
          </div>

          {log && log.rows?.length === 0 && (
            <div className="text-sm text-sage-muted sage-prose">
              No specs yet. When the Founder (or a Full Studio member) asks for one, it lands here.
            </div>
          )}

          {log?.rows?.map((r, i) => {
            const flagged = r.warden?.hallucination || r.warden?.sycophancy || r.warden?.waste;
            const open = openSpec === r.spec_id;
            const isCode = r.kind === "code";
            return (
              <div
                key={r.spec_id}
                className="border-t border-sage-border py-4 first:border-t-0 first:pt-0"
                data-testid={`build-log-row-${i}`}
              >
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <div className="text-sm font-bold text-sage-text flex-1 min-w-0">
                    <span className={`text-[10px] uppercase tracking-[0.15em] mr-2 px-1.5 py-0.5 border ${isCode ? "border-sage-accent text-sage-accent" : "border-sage-border text-sage-muted"}`}>
                      {isCode ? "code" : "spec"}
                    </span>
                    {r.feature}
                  </div>
                  <div className="flex gap-2 items-center">
                    {isCode && r.applied && (
                      <div className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border border-sage-accent text-sage-accent">applied</div>
                    )}
                    <div className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border ${flagged ? "border-sage-warn text-sage-warn" : "border-sage-accent text-sage-accent"}`}>
                      {flagged ? "flagged" : "clean"}
                    </div>
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mt-1 tabular-nums flex flex-wrap gap-x-4">
                  <span>{new Date(r.at).toLocaleString()}</span>
                  <span>{r.priority}</span>
                  <span>${(r.cost_usd ?? 0).toFixed(6)}</span>
                  <span>{(r.duration_ms/1000).toFixed(2)}s</span>
                  <span>{r.input_tokens} + {r.output_tokens} tok</span>
                  {isCode && r.files_touched?.length > 0 && (
                    <span>{r.files_touched.length} files · +{r.added_lines || 0}/-{r.removed_lines || 0}</span>
                  )}
                </div>
                <div className="flex gap-3 mt-2">
                  <button
                    onClick={() => setOpenSpec(open ? null : r.spec_id)}
                    data-testid={`build-log-expand-${i}`}
                    className="text-[10px] uppercase tracking-[0.1em] text-sage-accent hover:underline"
                  >
                    {open ? "Hide" : `Read the ${isCode ? "diff" : "spec"}`}
                  </button>
                  {isCode && isMaster() && (
                    <button
                      onClick={async () => {
                        try {
                          const r2 = await fetch(`${BACKEND_URL}/api/sage/admin/oven/dry-run/${r.spec_id}`, {
                            method: "POST",
                            headers: { Authorization: `Bearer ${getToken()}` },
                          });
                          if (!r2.ok) throw new Error(await r2.text());
                          const j = await r2.json();
                          toast[j.applies_clean && j.syntax_ok !== false ? "success" : "error"](
                            `Dry-run · ${j.reason || (j.applies_clean ? 'applies clean' : 'failed')}`
                          );
                          await load();
                        } catch (e) { toast.error(e.message); }
                      }}
                      data-testid={`build-log-dryrun-${i}`}
                      className="text-[10px] uppercase tracking-[0.1em] text-sage-accent hover:underline"
                    >
                      Dry-run
                    </button>
                  )}
                  {isCode && isMaster() && (
                    <button
                      onClick={async () => {
                        if (!confirm("Test Bench boots the patched backend on a private port and probes the new routes. Takes 10–25 seconds. Continue?")) return;
                        toast.message("Test Bench running — booting patched server…");
                        try {
                          const r2 = await fetch(`${BACKEND_URL}/api/sage/admin/oven/test-bench/${r.spec_id}`, {
                            method: "POST",
                            headers: { Authorization: `Bearer ${getToken()}` },
                          });
                          if (!r2.ok) throw new Error(await r2.text());
                          const j = await r2.json();
                          const flavor = j.overall === "green" ? "success" : j.overall === "yellow" ? "message" : "error";
                          toast[flavor](`Bench · ${j.overall.toUpperCase()} · ${j.reason || ""}`);
                          await load();
                        } catch (e) { toast.error(e.message); }
                      }}
                      data-testid={`build-log-bench-${i}`}
                      className="text-[10px] uppercase tracking-[0.1em] text-sage-accent hover:underline"
                    >
                      Test bench
                    </button>
                  )}
                  {isCode && !r.applied && isMaster() && (
                    <button
                      onClick={() => markApplied(r.spec_id)}
                      data-testid={`build-log-apply-${i}`}
                      className="text-[10px] uppercase tracking-[0.1em] text-sage-accent hover:underline"
                    >
                      Mark applied
                    </button>
                  )}
                </div>
                {isCode && r.dry_run && (
                  <div className="mt-2 text-[10px] uppercase tracking-[0.15em] tabular-nums" data-testid={`build-log-dryrun-verdict-${i}`}>
                    <span className={r.dry_run.applies_clean ? "text-sage-accent" : "text-sage-warn"}>
                      dry-run: {r.dry_run.applies_clean ? "applies" : "does not apply"}
                    </span>
                    {r.dry_run.syntax_ok === true && <span className="text-sage-accent"> · python compiles</span>}
                    {r.dry_run.syntax_ok === false && <span className="text-sage-warn"> · python errors</span>}
                    <span className="text-sage-faint normal-case tracking-normal"> · {r.dry_run.reason}</span>
                  </div>
                )}
                {isCode && r.test_bench && (
                  <div className="mt-1 text-[10px] uppercase tracking-[0.15em] tabular-nums" data-testid={`build-log-bench-verdict-${i}`}>
                    <span className={
                      r.test_bench.overall === "green" ? "text-sage-accent"
                      : r.test_bench.overall === "yellow" ? "text-sage-warn"
                      : "text-sage-warn"
                    }>
                      test-bench: {r.test_bench.overall}
                    </span>
                    {r.test_bench.boot_ok && (
                      <span className="text-sage-accent"> · booted in {r.test_bench.boot_duration_ms}ms</span>
                    )}
                    {r.test_bench.detected_new_routes?.length > 0 && (
                      <span className="text-sage-muted"> · {r.test_bench.detected_new_routes.length} new route{r.test_bench.detected_new_routes.length !== 1 ? "s" : ""} probed</span>
                    )}
                    <span className="text-sage-faint normal-case tracking-normal"> · {r.test_bench.reason}</span>
                  </div>
                )}
                {open && (
                  <pre
                    data-testid={`build-log-spec-${i}`}
                    className="mt-3 whitespace-pre-wrap text-xs text-sage-muted bg-sage-elevated p-4 border border-sage-border leading-relaxed max-h-[60vh] overflow-auto"
                  >
                    {r.diff || r.spec_text}
                  </pre>
                )}
              </div>
            );
          })}

          {!log && !err && (
            <div className="text-xs text-sage-faint" data-testid="oven-build-log-loading">Loading…</div>
          )}
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link
            to="/sage-ledger"
            className="text-xs uppercase tracking-[0.1em] border border-sage-accent text-sage-accent px-4 py-2 hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150"
          >
            See the public Ledger →
          </Link>
        </div>

        <div className="mt-12 text-[10px] uppercase tracking-[0.15em] text-sage-faint">
          Signed by Baker and Sage. Ratified by The Founder.
        </div>
      </div>
    </div>
  );
}
